import React from 'react';
import { useTranslation } from 'react-i18next';

import { PresencaResponse } from 'api/Assessor/presenca';

import { formatCPFOrCNPJ } from 'utils/cpfCnpjMask';
import { getDateFullTime, TIME_ZONE_SP } from 'utils/DateUtils';
import { PresencaDadaPor } from 'enuns/Presenca';

import { zonedTimeToUtc } from 'date-fns-tz';

import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
  useTheme,
  Tooltip,
} from '@material-ui/core';
import { UserIcon, PeopleIcon, PublicIcon } from 'components/Icons';

import useStyle from './style';
import TelIcon from 'components/Icons/TelIcon';
import { AssembleiaTipoOrigem } from 'enuns/AssembleiaOrigem';

interface Props {
  relatorio: PresencaResponse;
  page: number;
  size: number;
  onChangePage: (page: number) => void;
  onChangeSize: (size: number, page: number) => void;
}

export default function ({ relatorio, page, size, onChangePage, onChangeSize }: Props) {
  const { t } = useTranslation();
  const classes = useStyle();
  const theme = useTheme();

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, value: number) => {
    onChangePage(value + 1);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChangeSize(parseInt(event.target.value, 10), 1);
  };

  return (
    <TableContainer className={classes.root}>
      <Table>
        <TableHead className={classes.box}>
          <TableRow>
            <TableCell className={classes.title} align="center">
              {t('RelatorioPresentes.tableHead.nomeConvocado')}
            </TableCell>
            <TableCell className={classes.title} align="center">
              {t('RelatorioPresentes.tableHead.documento')}
            </TableCell>
            <TableCell className={classes.title} align="center">
              {t('RelatorioPresentes.tableHead.coop')}
            </TableCell>
            <TableCell className={classes.title} align="center">
              {t('RelatorioPresentes.tableHead.conta')}
            </TableCell>
            <TableCell className={classes.title} align="center">
              {t('RelatorioPresentes.tableHead.saldoCotas')}
            </TableCell>
            <TableCell className={classes.title} align="center">
              {t('RelatorioPresentes.tableHead.colaborador')}
            </TableCell>
            <TableCell className={classes.title} align="center">
              <Tooltip title={t('RelatorioPresentes.trTooltip.header') as string}>
                <span>{t('RelatorioPresentes.tableHead.tr')}</span>
              </Tooltip>
            </TableCell>
            <TableCell className={classes.title} align="center">
              {t('RelatorioPresentes.tableHead.origem')}
            </TableCell>
            <TableCell className={classes.title} align="center">
              {t('RelatorioPresentes.tableHead.presencaConfirmada')}
            </TableCell>
            <TableCell className={classes.title} align="center">
              {t('RelatorioPresentes.tableHead.dataHora')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody className={classes.box}>
          {relatorio.elements.map((row, index) => (
            <TableRow key={`${index}-${row.cpfCnpj}`}>
              <TableCell className={classes.data} align="left">
                {row.nomeConvocado}
              </TableCell>
              <TableCell className={[classes.data, classes.noBreakingData].join(' ')} align="center">
                {formatCPFOrCNPJ(row.cpfCnpj)}
              </TableCell>
              <TableCell className={classes.data} align="center">
                {row.cooperativa}
              </TableCell>
              <TableCell className={classes.data} align="center">
                {row.numeroConta}
              </TableCell>
              <TableCell className={classes.data} align="center">
                {row.saldoCotas.toString().replace('.', ',')}
              </TableCell>
              <TableCell className={classes.data} align="center">
                {t(`RelatorioPresentes.colaborador.${row.colaborador}`)}
              </TableCell>
              <TableCell className={classes.data} align="center">
                {row.tr === PresencaDadaPor.Representante ? (
                  <Tooltip title={t('RelatorioPresentes.trTooltip.R') as string}>
                    <span>
                      <PeopleIcon color={theme.palette.primary.main} />
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip title={t('RelatorioPresentes.trTooltip.T') as string}>
                    <span>
                      <UserIcon color={theme.palette.primary.main} />
                    </span>
                  </Tooltip>
                )}
              </TableCell>

              <TableCell className={classes.data} align="center">
                {row.flgOrigem === AssembleiaTipoOrigem.DIGITAL ? (
                  <Tooltip title={t('RelatorioPresentes.origemTooltip.digital') as string}>
                    <span>
                      <PublicIcon color={theme.palette.primary.main} />
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip title={t('RelatorioPresentes.origemTooltip.CanalVoz') as string}>
                    <span>
                      <TelIcon color={theme.palette.primary.main} />
                    </span>
                  </Tooltip>
                )}
              </TableCell>

              <TableCell className={classes.data} align="left">
                {row.presencaConfirmada}
              </TableCell>
              <TableCell className={classes.data} align="center">
                {getDateFullTime(zonedTimeToUtc(row.dataHoraPresenca, TIME_ZONE_SP))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              style={{ borderBottom: 'none' }}
              count={relatorio.totalElements}
              colSpan={9}
              rowsPerPageOptions={[10, 25, 50, 100]}
              rowsPerPage={size}
              page={page - 1}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              labelDisplayedRows={({ from, to, count }) =>
                t('RelatorioPresentes.labelDisplayedRows', { from, to, count })
              }
              labelRowsPerPage={t('RelatorioPresentes.labelRowsPerPage')}
              backIconButtonProps={{ className: classes.navIcon }}
              nextIconButtonProps={{ className: classes.navIcon }}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
